import { useEffect } from 'react'
import { useFetcher } from 'react-router'
import { useOptionalUser } from '#app/utils/user'

export interface UserRolesData {
	roles: string[]
	permissions: string[]
	userType: string
	/** Module keys the current org may use (e.g. lease_generator). */
	accessibleModules: string[]
	loading: boolean
	error?: string
}

/**
 * Hook to get current user's roles and permissions
 */
export function useUserRoles(): UserRolesData {
	const user = useOptionalUser()
	const fetcher = useFetcher<{
		roles: string[]
		permissions: string[]
		userType: string
		accessibleModules?: string[]
		error?: string
	}>()

	useEffect(() => {
		// Skip while logged out / mid-logout — avoids a useless /resources/user-roles
		// hit that previously 302'd to /tenant/login and spammed the console.
		if (!user) return
		if (fetcher.state === 'idle' && !fetcher.data) {
			fetcher.load('/resources/user-roles')
		}
	}, [fetcher, user])

	if (!user) {
		return {
			roles: [],
			permissions: [],
			userType: 'tenant',
			accessibleModules: [],
			loading: false,
		}
	}

	// Prefer fetched userType; fall back to session user so landlord nav does
	// not flash as the thin tenant set while /resources/user-roles loads.
	const resolvedType = fetcher.data?.userType ?? user.userType ?? 'tenant'

	return {
		roles: fetcher.data?.roles || [],
		permissions: fetcher.data?.permissions || [],
		userType: resolvedType,
		accessibleModules: fetcher.data?.accessibleModules ?? [],
		loading: fetcher.state !== 'idle' || !fetcher.data,
		error: fetcher.data?.error,
	}
}
